// Maya audio samples — pregenerated 30-second previews per (destination, lang).
//
// First request builds a destination-specific hook script from the cached
// DestinationGuide, synthesizes audio via Lovable AI TTS, uploads it to the
// private `maya-previews` bucket, caches the row in `maya_audio_samples`,
// and returns a signed URL.
//
// All later requests just read the row and return a fresh signed URL.
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { getDestinationGuide } from "@/lib/destination-guide.functions";


const LangSchema = z.enum(["es", "en", "fr", "pt"]);

const BUCKET = "maya-previews";
const SIGNED_URL_TTL_SECONDS = 60 * 60 * 24 * 7; // 7 days
const PREVIEW_MAX_PLAYS = 2;
const PREVIEW_WINDOW_HOURS = 24;

export interface MayaPreview {
  url: string | null;
  durationSeconds: number;
  transcript: string;
  hook: string;
  destination: string;
  playsRemaining: number;
  limitReached: boolean;
}

export interface MayaPreviewPlay {
  url: string;
  durationSeconds: number;
  playsRemaining: number;
}

const HOOK_PREFIX: Record<z.infer<typeof LangSchema>, (d: string) => string> = {
  es: (d) =>
    `Bienvenido a ${d}. Frente a ti se encuentra uno de los lugares más fascinantes de México. ¿Sabías que este destino guarda secretos que muy pocos conocen?`,
  en: (d) =>
    `Welcome to ${d}. Right in front of you lies one of the most captivating places in Mexico. Did you know this destination still hides secrets few travelers ever discover?`,
  fr: (d) =>
    `Bienvenue à ${d}. Devant toi se trouve l'un des lieux les plus fascinants du Mexique. Savais-tu que cette destination cache encore des secrets que peu de voyageurs découvrent ?`,
  pt: (d) =>
    `Bem-vindo a ${d}. Diante de você está um dos lugares mais fascinantes do México. Você sabia que este destino ainda guarda segredos que poucos viajantes descobrem?`,
};

const CONTINUATION: Record<z.infer<typeof LangSchema>, string> = {
  es: "Continúa escuchando con Maya Premium para conocer toda su historia, cultura, gastronomía y leyendas.",
  en: "Keep listening with Maya Premium to dive into its full history, culture, food and legends.",
  fr: "Continue avec Maya Premium pour découvrir toute son histoire, sa culture, sa gastronomie et ses légendes.",
  pt: "Continue ouvindo com Maya Premium para descobrir toda a sua história, cultura, gastronomia e lendas.",
};

function buildHookScript(
  destination: string,
  lang: z.infer<typeof LangSchema>,
  intro: string,
  _didYouKnow: string[],
): string {
  // Target ~35-45 words ≈ 15 seconds at relaxed pace.
  const opening = HOOK_PREFIX[lang](destination);
  const introSnippet = intro.trim().split(/(?<=[.!?])\s+/).slice(0, 1).join(" ");
  const body = [opening, introSnippet].filter(Boolean).join(" ");
  const trimmed = body.split(/\s+/).slice(0, 40).join(" ");
  return `${trimmed} ${CONTINUATION[lang]}`;
}

function safeSlug(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "destino";
}

async function admin() {
  const { supabaseAdmin } = await import(
    "@/integrations/supabase/client.server"
  );
  return supabaseAdmin;
}

async function signUrl(path: string, ttlSeconds = SIGNED_URL_TTL_SECONDS): Promise<string> {
  const sb = await admin();
  const { data, error } = await sb.storage
    .from(BUCKET)
    .createSignedUrl(path, ttlSeconds);
  if (error || !data?.signedUrl) {
    throw new Error(error?.message ?? "failed to sign preview url");
  }
  return data.signedUrl;
}

function clientIp(): string | null {
  try {
    const req = getRequest();
    const h = req.headers;
    const raw =
      h.get("cf-connecting-ip") ??
      h.get("x-real-ip") ??
      h.get("x-forwarded-for")?.split(",")[0].trim() ??
      null;
    return raw && raw.length <= 64 ? raw : null;
  } catch {
    return null;
  }
}

async function countRecentPlays(
  key: string,
  lang: string,
  deviceId: string | null,
  ip: string | null,
): Promise<number> {
  const sb = await admin();
  const since = new Date(Date.now() - PREVIEW_WINDOW_HOURS * 3600_000).toISOString();
  let query = sb
    .from("maya_preview_plays")
    .select("id", { count: "exact", head: true })
    .eq("destination", key)
    .eq("lang", lang)
    .gte("played_at", since);
  const filters: string[] = [];
  if (deviceId) filters.push(`device_id.eq.${deviceId}`);
  if (ip) filters.push(`ip.eq.${ip}`);
  if (filters.length === 0) return 0;
  query = query.or(filters.join(","));
  const { count } = await query;
  return count ?? 0;
}

/** Solo lectura: nunca genera texto ni audio (coste IA cero). */
async function readSample(
  key: string,
  lang: string,
): Promise<{ transcript: string; hook: string } | null> {
  const sb = await admin();
  const { data: row } = await sb
    .from("maya_audio_samples")
    .select("url, transcript, hook")
    .eq("destination", key)
    .eq("lang", lang)
    .maybeSingle();
  if (!row?.url) return null;
  return {
    transcript: (row.transcript as string | null) ?? "",
    hook: (row.hook as string | null) ?? "",
  };
}

async function ensureSample(
  key: string,
  lang: z.infer<typeof LangSchema>,
  destinationName: string,
  location: string | null,
  summary: string | null,
): Promise<{ objectPath: string; transcript: string; hook: string }> {
  const sb = await admin();
  const { data: row } = await sb
    .from("maya_audio_samples")
    .select("url, transcript, hook")
    .eq("destination", key)
    .eq("lang", lang)
    .maybeSingle();

  if (row?.url) {
    return {
      objectPath: row.url as string,
      transcript: (row.transcript as string | null) ?? "",
      hook: (row.hook as string | null) ?? "",
    };
  }

  const guide = await getDestinationGuide({
    data: { title: destinationName, location, summary, lang, generate: true },
  });
  const script = buildHookScript(
    guide.guide.destination ?? destinationName,
    lang,
    guide.guide.intro,
    guide.guide.didYouKnow ?? [],
  );
  const hook = HOOK_PREFIX[lang](guide.guide.destination ?? destinationName);

  const { synthesizeMayaSpeech } = await import("@/lib/maya-tts.server");
  const audio = await synthesizeMayaSpeech({
    text: script,
    lang,
    provider: "lovable",
    speed: 0.95,
  });
  const buffer = audio.body instanceof ArrayBuffer
    ? audio.body
    : await new Response(audio.body).arrayBuffer();

  const objectPath = `${safeSlug(key)}/${lang}.mp3`;
  const { error: upErr } = await sb.storage
    .from(BUCKET)
    .upload(objectPath, new Blob([buffer], { type: "audio/mpeg" }), {
      contentType: "audio/mpeg",
      upsert: true,
    });
  if (upErr) throw new Error(`maya preview upload failed: ${upErr.message}`);

  await sb.from("maya_audio_samples").upsert(
    {
      destination: key,
      lang,
      url: objectPath,
      duration_seconds: 15,
      transcript: script,
      hook,
      voice: "lovable/gpt-4o-mini-tts",
    },
    { onConflict: "destination,lang" },
  );

  return { objectPath, transcript: script, hook };
}

const DeviceIdSchema = z
  .string()
  .trim()
  .min(8)
  .max(80)
  .regex(/^[A-Za-z0-9_-]+$/);

/**
 * Returns metadata for the preview without exposing the audio URL.
 * The URL is only issued by `startMayaPreviewPlay` after the server-side
 * play-count check succeeds. This prevents bypass via localStorage tampering.
 */
export const getMayaPreview = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        destinationKey: z.string().trim().min(1).max(160),
        destinationName: z.string().trim().min(1).max(160),
        location: z.string().trim().max(160).nullable().optional(),
        summary: z.string().trim().max(2000).nullable().optional(),
        lang: LangSchema.default("es"),
        deviceId: DeviceIdSchema.optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<MayaPreview> => {
    const lang = data.lang as z.infer<typeof LangSchema>;
    const key = data.destinationKey.trim().toLowerCase();

    // COSTE IA: esta llamada ocurre al montar la ficha del tour, así que NO
    // genera nada. El guion y el audio se crean en `startMayaPreviewPlay`,
    // es decir, solo cuando el viajero pulsa "Escuchar muestra".
    const sample = await readSample(key, lang);

    const ip = clientIp();
    const deviceId = data.deviceId ?? null;
    const used = await countRecentPlays(key, lang, deviceId, ip);
    const remaining = Math.max(0, PREVIEW_MAX_PLAYS - used);

    return {
      url: null,
      durationSeconds: 15,
      transcript: sample?.transcript ?? "",
      hook: sample?.hook ?? "",
      destination: data.destinationName,
      playsRemaining: remaining,
      limitReached: remaining <= 0,
    };
  });

/**
 * Atomically checks the server-side play quota, records a play row and, if
 * allowed, returns a short-lived signed URL for the audio file.
 * Throws `PREVIEW_LIMIT_REACHED` when the caller has already reached the
 * per-device / per-IP limit within PREVIEW_WINDOW_HOURS.
 */
export const startMayaPreviewPlay = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        destinationKey: z.string().trim().min(1).max(160),
        destinationName: z.string().trim().min(1).max(160),
        location: z.string().trim().max(160).nullable().optional(),
        summary: z.string().trim().max(2000).nullable().optional(),
        lang: LangSchema.default("es"),
        deviceId: DeviceIdSchema,
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<MayaPreviewPlay> => {
    const lang = data.lang as z.infer<typeof LangSchema>;
    const key = data.destinationKey.trim().toLowerCase();
    const ip = clientIp();
    const deviceId = data.deviceId;

    const used = await countRecentPlays(key, lang, deviceId, ip);
    if (used >= PREVIEW_MAX_PLAYS) {
      throw new Error("PREVIEW_LIMIT_REACHED");
    }

    const sample = await ensureSample(
      key,
      lang,
      data.destinationName,
      data.location ?? null,
      data.summary ?? null,
    );

    const sb = await admin();
    await sb.from("maya_preview_plays").insert({
      destination: key,
      lang,
      device_id: deviceId,
      ip,
    });

    // Short-lived URL so the client can't reuse it later to bypass the quota.
    const signed = await signUrl(sample.objectPath, 120);
    return {
      url: signed,
      durationSeconds: 15,
      playsRemaining: Math.max(0, PREVIEW_MAX_PLAYS - used - 1),
    };
  });
